<template>
  <div class="actual-rent-container">
    <div class="grid-controls">
      <div class="level-controls">
        <res-radio-group 
          label="Actual Rent calculations are being shown at the"
          :value="trackActualRentAtUnitTypeLevel"
          :options="trackActualRentLevelOptions"
          :readonly="true || readonly">
        </res-radio-group>
      </div>
      <div class="help-controls">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" @click="openHelp(null)" v-on="on" class="pb-2" color="var(--v-resBlue-darken1)">mdi-help-circle</v-icon>
          </template>
          <span>Help</span>
        </v-tooltip>
        <res-dialog :showDialog="showHelp"
                    titleClass="resBlue darken-1 white--text"
                    iconColor="white"
                    cancelButtonText="Close"
                    :cancelButtonOnly="true"
                    cancelButtonColor="resBlue darken-1"
                    :maxWidth="800"
                    :setWidth="800"
                    :setHeight="600"
                    attach=".actual-rent-container"
                    @cancel="closeHelp">
          <occupancy-actual-rent-help :openSection="openHelpSection"></occupancy-actual-rent-help>
        </res-dialog>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" class="ml-3 mb-2" :color="commentIconColor" @click="openComments">{{ commentIcon  }}</v-icon>
          </template>
          <span>Comments/Flags</span>
        </v-tooltip>
        <v-menu left offset-y close-on-click @input="updateMenuState">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon :color="menuColor" class="ellipses-control">
              <v-icon dense>{{menuIcon}}</v-icon>
            </v-btn>
          </template>
          <v-list class="ellipses-control-option-list">
            <v-list-item @click="openComments">
              <v-list-item-title class="ellipses-control-option">Add Flag or Comment</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-list-item v-bind="attrs" v-on="on" @click="openClearResetDialog">
                  <v-list-item-title class="ellipses-control-option">Clear & Reset</v-list-item-title>
                </v-list-item>
              </template>
              <span>This will clear this tab of any entered information and revert to the initial values</span>
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
    </div>
    <!-- Summary -->
    <ag-grid-vue 
      style="width: 100%; height: 198px;"
      class="ag-theme-material"
      :gridOptions="summaryGridOptions"
      :columnDefs="currentHeaders"
      :rowData="currentSummaryData">
    </ag-grid-vue>
    <!-- Details -->    
    <div style="height: 1000px; margin-top: 16px;">
      <ag-grid-vue 
        style="width: 100%; height: 100%;"
        class="ag-theme-material"
        :gridOptions="detailGridOptions"
        :columnDefs="currentHeaders"
        :rowData="currentDetailData"
        @cell-value-changed="onCellValueChanged">
      </ag-grid-vue>
    </div>
    <!-- Dialogs -->
    <clear-all-dialog 
      v-if="showClearResetDialog"
      :showDialog="showClearResetDialog"
      :maxWidth="480"
      :setWidth="480"
      @confirm="confirmClearResetDialog"
      @cancel="closeClearResetDialog">
    </clear-all-dialog>
    <clear-all-dialog 
      v-if="showChangeTrackingLevelDialog"
      :showDialog="showChangeTrackingLevelDialog"
      :maxWidth="480"
      :setWidth="480"
      altText="This will clear all data from this tab and add change the fields to the selected level. Are you sure you want to do this?"
      @confirm="confirmChangeTrackingLevelDialog"
      @cancel="cancelChangeTrackingLevelDialog">
    </clear-all-dialog>
  </div>
</template>

<script>  
  // Row Types
  const SUMMARY_GPR = 1;
  const SUMMARY_LESS_VACANCY_LOSS = 2;
  const SUMMARY_LESS_ACTUAL_RENT = 3;
  const SUMMARY_GAIN_LOSS_TO_LEASE = 4;

  const UNIT_TYPES_HEADER = 5;
  
  const AVERAGE_GPR = 6;
  const AVERAGE_CONTRACTED_RENT = 7;

  const RENEWAL_LEASES = 8;
  const RENEWAL_RENT_INCREASE_PERCENTAGE = 9;
  const RENEWAL_RENT_INCREASE_AMOUNT = 10;
  const AVERAGE_RENEWAL_RENT = 11;
  const SUBTOTAL_RENEWAL_RENT = 12;

  const NEW_MOVE_IN_LEASES = 13;
  const NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE = 14;
  const NEW_MOVE_IN_RENT_DISCOUNT_AMOUNT = 15;
  const AVERAGE_NEW_MOVE_IN_RENT = 16;
  const SUBTOTAL_NEW_MOVE_IN_RENT = 17;

  const FORECASTED_LEASES = 18;
  const SUBTOTAL_FORECASTED_RENT = 19;
  
  const ESTIMATED_CONTRACTED_LEASES = 20;
  const SUBTOTAL_ESTIMATED_CONTRACTED_RENT = 21;

  const PROJECTED_ACTUAL_RENT = 22;

  import YearGrid from '@/mixins/YearGrid';

  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog";
  import ResDialog from '@/components/base/ResDialog.vue';
  import { AgGridVue } from "ag-grid-vue";
  import UnitTypeGridHeader from '@/components/budgets/worksheet/tabs/common/UnitTypeGridHeader';
  import ConditionalIconCellRenderer from '@/components/base/ag-grid/ConditionalIconCellRenderer';
  import LabelCellRenderer from '@/components/base/ag-grid/LabelCellRenderer';

  import budgetService from '@/services/budget';
  import occupancyService from '@/services/occupancy';
  import worksheetService from '@/services/worksheet';
  import budgetGLAccountService from '@/services/budget-gl-account';

  import OccupancyActualRentHelp from '@/components/help/OccupancyActualRentHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';

  import { Constants } from '@/utilities/constants/index.js';
  import { mapActions } from 'vuex';

  export default {
    name: 'ActualRent',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      ResRadioGroup,
      ClearAllDialog,
      ResDialog,
      OccupancyActualRentHelp,
      AgGridVue,
      /* eslint-disable vue/no-unused-components */
      UnitTypeGridHeader,               // used by AG Grid
      ConditionalIconCellRenderer,      // used by AG Grid
      LabelCellRenderer                 // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: { 
        required: true
      },
      commentIteration: {},
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        budgetIterationLastModifiedTimestamp: null,
        isCalculating: false,
        summaryRefreshIsQueued: false,
        trackActualRentLevelOptions: [
          { label: 'Property Level', value: false },
          { label: 'Unit Type Level', value: true },
        ],
        trackActualRentAtUnitTypeLevel: false,
        trackMoveInsMoveOutsAtUnitTypeLevel: false,
        renewAtMarketRent: false,
        moveInAtMarketRent: false,
        fiscalYear: null,
        fiscalYearStartMonth: null,
        fiscalYearHeaders: [],
        availableColumns: [],
        summaryGridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          columnDefs: this.availableColumns,
          alignedGrids: [],
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: this.shouldShowRow,
          frameworkComponents: {
            'ConditionalIconCellRenderer': 'ConditionalIconCellRenderer'
          }
        },
        summaryData: [],
        detailGridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          columnDefs: this.availableColumns,
          alignedGrids: [],
          singleClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: this.shouldShowRow,
          isFullWidthCell: (params) => { return params.data.rowType == UNIT_TYPES_HEADER; },
          fullWidthCellRendererFramework: 'UnitTypeGridHeader',
          frameworkComponents: {
            'ConditionalIconCellRenderer': 'ConditionalIconCellRenderer',
            'LabelCellRenderer': 'LabelCellRenderer'
          }
        },
        detailData: [],
        showClearResetDialog: false,
        showChangeTrackingLevelDialog: false,
        tabHasUnresolvedComments: false,
        tabHasComments: false,
        menuOpen: false
      };
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      updateMenuState(isOpen) {
        this.menuOpen = isOpen;
      },
      // DOM load and update logic
      async load() {
        let t = this;

        t.showGridLoadingOverlay(t.summaryGridOptions);
        t.showGridLoadingOverlay(t.detailGridOptions);

        await t.loadGridConfiguration();
        await t.loadActualRentData();

        t.sizeGridColumns(t.summaryGridOptions);
        t.sizeGridColumns(t.detailGridOptions);
        t.hideGridLoadingOverlay(t.summaryGridOptions);
        t.hideGridLoadingOverlay(t.detailGridOptions);
      },
      async loadGridConfiguration() {
        let t = this;

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;
        t.moveInAtMarketRent = settings.newLeaseCalculationOption != 1;
        t.renewAtMarketRent = settings.renewalPricingCalculationOption != 1;

        // setup column headers
        t.fiscalYearHeaders = t.getFiscalYearMonths(t.fiscalYearStartMonth);
        t.availableColumns = [
          { 
            headerName: "", field: "isUnitTypeCollapsed", width: 16, pinned: 'left', suppressSizeToFit: true, 
            cellClass: 'readonly-column',
            cellStyle: { margin: 0, padding: 0 },
            cellRenderer: () => '<span></span>'
          },
          { 
            headerName: "", field: "label", width: 240, pinned: 'left', suppressSizeToFit: true,
            cellClass: 'readonly-column',
            cellRendererSelector: t.selectLabelCellRenderer,
            cellStyle: function(params) {
                var style = { 'margin-left': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'right' };
                if (params.data.subLabel) {
                  style['line-height'] = 'normal'; 
              }
                return style;
            }
          },
          { headerName: "", field: "year", width: 60, pinned: 'left', suppressSizeToFit: true,
            cellClass: 'readonly-column',
            cellStyle: { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' }
          },
          { headerName: t.fiscalYearHeaders[0], field: "periodIndex00Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[1], field: "periodIndex01Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[2], field: "periodIndex02Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[3], field: "periodIndex03Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[4], field: "periodIndex04Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[5], field: "periodIndex05Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[6], field: "periodIndex06Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[7], field: "periodIndex07Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[8], field: "periodIndex08Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[9], field: "periodIndex09Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[10], field: "periodIndex10Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[11], field: "periodIndex11Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: "TOTAL/AVG", field: "totalOrAverage", width: 96, pinned: 'right', valueFormatter: t.cellFormatter, cellClass: 'readonly-column' }
        ];

        // align grids
        t.summaryGridOptions.alignedGrids = [t.detailGridOptions];
        t.detailGridOptions.alignedGrids = [t.summaryGridOptions];
      },
      async loadActualRentData() {
        let t = this;

        let response = await occupancyService.getActualRentEntries(t.budgetIterationId);

        t.trackActualRentAtUnitTypeLevel = response.trackActualRentAtUnitTypeLevel;
        t.trackMoveInsMoveOutsAtUnitTypeLevel = response.trackMoveInsMoveOutsAtUnitTypeLevel;

        // Load Summary Data
        t.summaryData = [];

        // Gross Potential Rent
        t.summaryData.push(t.initRow(SUMMARY_GPR, null, response.summary.grossPotentialRent));
        // Less Vacancy Loss
        t.summaryData.push(t.initRow(SUMMARY_LESS_VACANCY_LOSS, null, response.summary.vacancyLoss));
        // Less Actual Rent
        t.summaryData.push(t.initRow(SUMMARY_LESS_ACTUAL_RENT, null, response.summary.actualRent));
        // (GAIN)/LOSS TO LEASE
        t.summaryData.push(t.initRow(SUMMARY_GAIN_LOSS_TO_LEASE, null, response.summary.lossGainToLease));

        // Load Detail Data
        t.detailData = [];

        let details = t.trackActualRentAtUnitTypeLevel ? response.unitTypeLevelEntries : [response.propertyLevelEntries];
        (details).forEach((d) => {
          let unitTypeId = null;
          if (t.trackActualRentAtUnitTypeLevel) {
            unitTypeId = d.unitTypeId;
            // add unit type header
            t.detailData.push(t.initUnitTypesGridHeaderRow(UNIT_TYPES_HEADER, unitTypeId, d.name));
          }

          // Average Market Rent
          t.detailData.push(t.initRow(AVERAGE_GPR, unitTypeId, d.averageGrossPotentialRent));

          // Average Contracted Rent
          t.detailData.push(t.initRow(AVERAGE_CONTRACTED_RENT, unitTypeId, d.averageContractedRent));

          // # Renewals
          t.detailData.push(t.initRow(RENEWAL_LEASES, unitTypeId, d.renewalLeases));

          // Renewal Rent Increase %
          t.detailData.push(t.initRow(RENEWAL_RENT_INCREASE_PERCENTAGE, unitTypeId, d.renewalRentIncreasePercentage));

          // Renewal Rent Increase $
          t.detailData.push(t.initRow(RENEWAL_RENT_INCREASE_AMOUNT, unitTypeId, d.renewalRentIncreaseAmount));

          // Average Renewal Rent
          if (t.renewAtMarketRent == false) {
            t.detailData.push(t.initRow(AVERAGE_RENEWAL_RENT, unitTypeId, d.averageRenewalRent));
          }

          // Subtotal Renewal Rent
          t.detailData.push(t.initRow(SUBTOTAL_RENEWAL_RENT, unitTypeId, d.subtotalRenewalRent));

          // # New Lease (Move-Ins)
          t.detailData.push(t.initRow(NEW_MOVE_IN_LEASES, unitTypeId, d.newMoveInLeases));

          // New Move-In Discount %
          t.detailData.push(t.initRow(NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE, unitTypeId, d.newMoveInRentDiscountPercentage));

          if (t.moveInAtMarketRent == false) {
            // New Move-In Discount $
            t.detailData.push(t.initRow(NEW_MOVE_IN_RENT_DISCOUNT_AMOUNT, unitTypeId, d.newMoveInRentDiscountAmount));

            // Average New Lease Rent
            t.detailData.push(t.initRow(AVERAGE_NEW_MOVE_IN_RENT, unitTypeId, d.averageNewMoveInRent));
          }

          // Subtotal New Lease Rent
          t.detailData.push(t.initRow(SUBTOTAL_NEW_MOVE_IN_RENT, unitTypeId, d.subtotalNewMoveInRent));

          // # Forecasted Leases
          t.detailData.push(t.initRow(FORECASTED_LEASES, unitTypeId, d.forecastedLeases));

          // Subtotal Forecasted Rent
          t.detailData.push(t.initRow(SUBTOTAL_FORECASTED_RENT, unitTypeId, d.subtotalForecastedRent));

          // # Estimated Contracted Leases
          t.detailData.push(t.initRow(ESTIMATED_CONTRACTED_LEASES, unitTypeId, d.estimatedContractedLeases));

          // Subtotal Estimated Contracted Rent
          t.detailData.push(t.initRow(SUBTOTAL_ESTIMATED_CONTRACTED_RENT, unitTypeId, d.subtotalEstimatedContractedRent));

          // TOTAL ACTUAL RENT
          t.detailData.push(t.initRow(PROJECTED_ACTUAL_RENT, unitTypeId, d.projectedActualRent));
        });
      },
      async refreshActualRentData() {
        let t = this;

        t.showGridLoadingOverlay(t.summaryGridOptions);
        t.showGridLoadingOverlay(t.detailGridOptions);

        // Retrieve latest data from server
        let response = await occupancyService.getActualRentEntries(t.budgetIterationId);

        if (response.trackActualRentAtUnitTypeLevel != t.trackActualRentAtUnitTypeLevel) {
          await t.loadActualRentData(); // if the tracking level changed behind the scenes need to reload everything
          return;
        }

        // Refresh Summary Data
        let summaryRowDataToUpdate = [];
        
        let grossPotentialRentRowData = t.summaryGridOptions.api.getRowNode(t.generateRowKey(SUMMARY_GPR, null, t.fiscalYear)).data;
        let vacancyLossRowData = t.summaryGridOptions.api.getRowNode(t.generateRowKey(SUMMARY_LESS_VACANCY_LOSS, null, t.fiscalYear)).data;
        let actualRentRowData = t.summaryGridOptions.api.getRowNode(t.generateRowKey(SUMMARY_LESS_ACTUAL_RENT, null, t.fiscalYear)).data;
        let lossGainToLeaseRowData = t.summaryGridOptions.api.getRowNode(t.generateRowKey(SUMMARY_GAIN_LOSS_TO_LEASE, null, t.fiscalYear)).data;

        for (let i = 0; i < 12; i++) {
          // Refresh Gross Potential Rent
          let grossPotentialRentVal = t.getPeriodIndexAmount(response.summary.grossPotentialRent, i);
          t.setRowNodePeriodIndexAmount(grossPotentialRentRowData, i, grossPotentialRentVal);

          // Refresh Less Vacancy Loss
          let vacancyLossVal = t.getPeriodIndexAmount(response.summary.vacancyLoss, i);
          t.setRowNodePeriodIndexAmount(vacancyLossRowData, i, vacancyLossVal);

          // Refresh Less Actual Rent
          let actualRentVal = t.getPeriodIndexAmount(response.summary.actualRent, i);
          t.setRowNodePeriodIndexAmount(actualRentRowData, i, actualRentVal);

          // Refresh (GAIN)/LOSS TO LEASE
          let lossGainToLeaseVal = t.getPeriodIndexAmount(response.summary.lossGainToLease, i);
          t.setRowNodePeriodIndexAmount(lossGainToLeaseRowData, i, lossGainToLeaseVal);
        }

        // Refresh Totals
        grossPotentialRentRowData.totalOrAverage = t.calculateTotalOrAverage(grossPotentialRentRowData);
        vacancyLossRowData.totalOrAverage = t.calculateTotalOrAverage(vacancyLossRowData);
        actualRentRowData.totalOrAverage = t.calculateTotalOrAverage(actualRentRowData);
        lossGainToLeaseRowData.totalOrAverage = t.calculateTotalOrAverage(lossGainToLeaseRowData);

        // Queue Updates
        summaryRowDataToUpdate.push(grossPotentialRentRowData);
        summaryRowDataToUpdate.push(vacancyLossRowData);
        summaryRowDataToUpdate.push(actualRentRowData);
        summaryRowDataToUpdate.push(lossGainToLeaseRowData);

        // Apply Updates
        t.summaryGridOptions.api.applyTransactionAsync({ update: summaryRowDataToUpdate });

        // Refresh Detail Data
        let rowDataToUpdate = [];

        let details = t.trackActualRentAtUnitTypeLevel ? response.unitTypeLevelEntries : [response.propertyLevelEntries];
        (details).forEach((detail) => {
          let unitTypeId = detail.unitTypeId; // null denotes property level

          let averageGrossPotentialRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(AVERAGE_GPR, unitTypeId, t.fiscalYear)).data;
          let averageContractedRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(AVERAGE_CONTRACTED_RENT, unitTypeId, t.fiscalYear)).data;
          let renewalLeasesRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(RENEWAL_LEASES, unitTypeId, t.fiscalYear)).data;
          let renewalRentIncreasePercentageRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(RENEWAL_RENT_INCREASE_PERCENTAGE, unitTypeId, t.fiscalYear)).data;
          let renewalRentIncreaseAmountRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(RENEWAL_RENT_INCREASE_AMOUNT, unitTypeId, t.fiscalYear)).data;
          let averageRenewalRentRowData = t.renewAtMarketRent == false ? t.detailGridOptions.api.getRowNode(t.generateRowKey(AVERAGE_RENEWAL_RENT, unitTypeId, t.fiscalYear)).data : null;
          let subtotalRenewalRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(SUBTOTAL_RENEWAL_RENT, unitTypeId, t.fiscalYear)).data;
          let newMoveInLeasesRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(NEW_MOVE_IN_LEASES, unitTypeId, t.fiscalYear)).data;
          let newMoveInRentDiscountPercentageRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE, unitTypeId, t.fiscalYear)).data;
          let newMoveInRentDiscountAmountRowData = t.moveInAtMarketRent == false ? t.detailGridOptions.api.getRowNode(t.generateRowKey(NEW_MOVE_IN_RENT_DISCOUNT_AMOUNT, unitTypeId, t.fiscalYear)).data : null;
          let averageNewMoveInRentRowData = t.moveInAtMarketRent == false ? t.detailGridOptions.api.getRowNode(t.generateRowKey(AVERAGE_NEW_MOVE_IN_RENT, unitTypeId, t.fiscalYear)).data : null;
          let subtotalNewMoveInRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(SUBTOTAL_NEW_MOVE_IN_RENT, unitTypeId, t.fiscalYear)).data;
          let forecastedLeasesRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(FORECASTED_LEASES, unitTypeId, t.fiscalYear)).data;
          let subtotalForecastedRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(SUBTOTAL_FORECASTED_RENT, unitTypeId, t.fiscalYear)).data;
          let estimatedContractedLeasesRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(ESTIMATED_CONTRACTED_LEASES, unitTypeId, t.fiscalYear)).data;
          let subtotalEstimatedContractedRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(SUBTOTAL_ESTIMATED_CONTRACTED_RENT, unitTypeId, t.fiscalYear)).data;
          let projectedActualRentRowData = t.detailGridOptions.api.getRowNode(t.generateRowKey(PROJECTED_ACTUAL_RENT, unitTypeId, t.fiscalYear)).data;

          for (let i = 0; i < 12; i++) {
            // Average Market Rent
            let averageGrossPotentialRentVal = t.getPeriodIndexAmount(detail.averageGrossPotentialRent, i);
            t.setRowNodePeriodIndexAmount(averageGrossPotentialRentRowData, i, averageGrossPotentialRentVal);

            // Average Contracted Rent
            let averageContractedRentVal = t.getPeriodIndexAmount(detail.averageContractedRent, i);
            t.setRowNodePeriodIndexAmount(averageContractedRentRowData, i, averageContractedRentVal);

            // # Renewals
            let renewalLeasesVal = t.getPeriodIndexAmount(detail.renewalLeases, i);
            t.setRowNodePeriodIndexAmount(renewalLeasesRowData, i, renewalLeasesVal);

            // Renewal Rent Increase %
            let renewalRentIncreasePercentageVal = t.getPeriodIndexAmount(detail.renewalRentIncreasePercentage, i);
            t.setRowNodePeriodIndexAmount(renewalRentIncreasePercentageRowData, i, renewalRentIncreasePercentageVal);

            // Renewal Rent Increase $
            let renewalRentIncreaseAmountVal = t.getPeriodIndexAmount(detail.renewalRentIncreaseAmount, i);
            t.setRowNodePeriodIndexAmount(renewalRentIncreaseAmountRowData, i, renewalRentIncreaseAmountVal);

            // Average Renewal Rent
            if (averageRenewalRentRowData) {
              let averageRenewalRentVal = t.getPeriodIndexAmount(detail.averageRenewalRent, i);
              t.setRowNodePeriodIndexAmount(averageRenewalRentRowData, i, averageRenewalRentVal);
            }

            // Subtotal Renewal Rent
            let subtotalRenewalRentVal = t.getPeriodIndexAmount(detail.subtotalRenewalRent, i);
            t.setRowNodePeriodIndexAmount(subtotalRenewalRentRowData, i, subtotalRenewalRentVal);

            // # New Leases (Move-Ins)
            let newMoveInLeasesVal = t.getPeriodIndexAmount(detail.newMoveInLeases, i);
            t.setRowNodePeriodIndexAmount(newMoveInLeasesRowData, i, newMoveInLeasesVal);

            // New Move-Ins Discount %
            let newMoveInRentDiscountPercentageVal = t.getPeriodIndexAmount(detail.newMoveInRentDiscountPercentage, i);
            t.setRowNodePeriodIndexAmount(newMoveInRentDiscountPercentageRowData, i, newMoveInRentDiscountPercentageVal);

            // New Move-In Discount $
            if (newMoveInRentDiscountAmountRowData) {
              let newMoveInRentDiscountAmountVal = t.getPeriodIndexAmount(detail.newMoveInRentDiscountAmount, i);
              t.setRowNodePeriodIndexAmount(newMoveInRentDiscountAmountRowData, i, newMoveInRentDiscountAmountVal);
            }

            // Average New Lease Rent
            if (averageNewMoveInRentRowData) {
              let averageNewMoveInRentVal = t.getPeriodIndexAmount(detail.averageNewMoveInRent, i);
              t.setRowNodePeriodIndexAmount(averageNewMoveInRentRowData, i, averageNewMoveInRentVal);
            }

            // Subtotal New Lease Rent
            let subtotalNewMoveInRentVal = t.getPeriodIndexAmount(detail.subtotalNewMoveInRent, i);
            t.setRowNodePeriodIndexAmount(subtotalNewMoveInRentRowData, i, subtotalNewMoveInRentVal);

            // # Forecasted Leases
            let forecastedLeasesVal = t.getPeriodIndexAmount(detail.forecastedLeases, i);
            t.setRowNodePeriodIndexAmount(forecastedLeasesRowData, i, forecastedLeasesVal);

            // Subtotal Forecasted Rent
            let subtotalForecastedRentVal = t.getPeriodIndexAmount(detail.subtotalForecastedRent, i);
            t.setRowNodePeriodIndexAmount(subtotalForecastedRentRowData, i, subtotalForecastedRentVal);

            // # Estimated Contracted Leases
            let estimatedContractedLeasesVal = t.getPeriodIndexAmount(detail.estimatedContractedLeases, i);
            t.setRowNodePeriodIndexAmount(estimatedContractedLeasesRowData, i, estimatedContractedLeasesVal);

            // Subtotal Estimated Contracted Rent
            let subtotalEstimatedContractedRentVal = t.getPeriodIndexAmount(detail.subtotalEstimatedContractedRent, i);
            t.setRowNodePeriodIndexAmount(subtotalEstimatedContractedRentRowData, i, subtotalEstimatedContractedRentVal);

            // TOTAL ACTUAL RENT
            let projectedActualRentVal = t.getPeriodIndexAmount(detail.projectedActualRent, i);
            t.setRowNodePeriodIndexAmount(projectedActualRentRowData, i, projectedActualRentVal);
          }

          // Refresh Totals
          averageGrossPotentialRentRowData.totalOrAverage = t.calculateTotalOrAverage(averageGrossPotentialRentRowData);
          averageContractedRentRowData.totalOrAverage = t.calculateTotalOrAverage(averageContractedRentRowData);
          renewalRentIncreaseAmountRowData.totalOrAverage = t.calculateTotalOrAverage(renewalRentIncreaseAmountRowData);
          renewalLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(renewalLeasesRowData);
          renewalRentIncreasePercentageRowData.totalOrAverage = t.calculateTotalOrAverage(renewalRentIncreasePercentageRowData);
          renewalRentIncreaseAmountRowData.totalOrAverage = t.calculateTotalOrAverage(renewalRentIncreaseAmountRowData);
          if (averageRenewalRentRowData) {
            averageRenewalRentRowData.totalOrAverage = t.calculateTotalOrAverage(averageRenewalRentRowData);
          }
          subtotalRenewalRentRowData.totalOrAverage = t.calculateTotalOrAverage(subtotalRenewalRentRowData);
          newMoveInLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(newMoveInLeasesRowData);
          newMoveInRentDiscountPercentageRowData.totalOrAverage = t.calculateTotalOrAverage(newMoveInRentDiscountPercentageRowData);
          if (newMoveInRentDiscountAmountRowData) {
            newMoveInRentDiscountAmountRowData.totalOrAverage = t.calculateTotalOrAverage(newMoveInRentDiscountAmountRowData);
          }
          if (averageNewMoveInRentRowData) {
            averageNewMoveInRentRowData.totalOrAverage = t.calculateTotalOrAverage(averageNewMoveInRentRowData);
          }
          subtotalNewMoveInRentRowData.totalOrAverage = t.calculateTotalOrAverage(subtotalNewMoveInRentRowData);
          forecastedLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(forecastedLeasesRowData);
          subtotalForecastedRentRowData.totalOrAverage = t.calculateTotalOrAverage(subtotalForecastedRentRowData);
          estimatedContractedLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedContractedLeasesRowData);
          subtotalEstimatedContractedRentRowData.totalOrAverage = t.calculateTotalOrAverage(subtotalEstimatedContractedRentRowData);
          projectedActualRentRowData.totalOrAverage = t.calculateTotalOrAverage(projectedActualRentRowData);

          // Queue Updates
          rowDataToUpdate.push(averageGrossPotentialRentRowData);
          rowDataToUpdate.push(averageContractedRentRowData);
          rowDataToUpdate.push(renewalLeasesRowData);
          rowDataToUpdate.push(renewalRentIncreasePercentageRowData);
          rowDataToUpdate.push(renewalRentIncreaseAmountRowData);
          if (averageRenewalRentRowData) {
            rowDataToUpdate.push(averageRenewalRentRowData);
          }
          rowDataToUpdate.push(subtotalRenewalRentRowData);
          rowDataToUpdate.push(newMoveInLeasesRowData);
          rowDataToUpdate.push(newMoveInRentDiscountPercentageRowData);
          if (newMoveInRentDiscountAmountRowData) {
            rowDataToUpdate.push(newMoveInRentDiscountAmountRowData);
          }
          if (averageNewMoveInRentRowData) {
            rowDataToUpdate.push(averageNewMoveInRentRowData);
          }
          rowDataToUpdate.push(subtotalNewMoveInRentRowData);
          rowDataToUpdate.push(forecastedLeasesRowData);
          rowDataToUpdate.push(subtotalForecastedRentRowData);
          rowDataToUpdate.push(estimatedContractedLeasesRowData);
          rowDataToUpdate.push(subtotalEstimatedContractedRentRowData);
          rowDataToUpdate.push(projectedActualRentRowData);
        });

        // Apply Updates
        t.detailGridOptions.api.applyTransactionAsync({ update: rowDataToUpdate });

        t.hideGridLoadingOverlay(t.summaryGridOptions);
        t.hideGridLoadingOverlay(t.detailGridOptions);
      },
      // row initialization logic
      initRow(rowType, unitTypeId, data) {
        return {
          // identifiers
          rowType: rowType,
          unitTypeId: unitTypeId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,

          // displayed columns
          label: data.label,
          subLabel: data.subLabel,
          year: data.year,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          totalOrAverage: this.getTotalOrAverage(rowType, data),

          // grouping metadata
          isGroupParent: false,
          isUnitTypeCollapsed: true
        }
      },
      initUnitTypesGridHeaderRow(rowType, unitTypeId, name) {
        let t = this;
        return {
          // identifiers
          rowType: rowType,
          unitTypeId: unitTypeId,
          name: name,

          // grouping metadata
          isGroupParent: true,
          isUnitTypeCollapsed: true, // collapsed by default

          // custom params for full width renderer component (UnitTypeGroupHeader)
          allCollapsed: function(params) {
            return params.data.isUnitTypeCollapsed;
          },
          onClick: function(params) {
            let show = !params.data.isUnitTypeCollapsed;
            params.api.forEachNode(node => {
              if (node.data.unitTypeId == params.data.unitTypeId) {
                node.setDataValue('isUnitTypeCollapsed', show);
              }
            });
            t.refreshDataFilter();
          }
        };
      },
      getPeriodIndexAmount(data, periodIndex) {
        switch (periodIndex) {
          case 0:
            return data.periodIndex00.amount;
          case 1:
            return data.periodIndex01.amount;
          case 2:
            return data.periodIndex02.amount;
          case 3:
            return data.periodIndex03.amount;
          case 4:
            return data.periodIndex04.amount;
          case 5:
            return data.periodIndex05.amount;
          case 6:
            return data.periodIndex06.amount;
          case 7:
            return data.periodIndex07.amount;
          case 8:
            return data.periodIndex08.amount;
          case 9:
            return data.periodIndex09.amount;
          case 10:
            return data.periodIndex10.amount;
          case 11:
            return data.periodIndex11.amount;
          default:
            break;
        }
      },
      getTotalOrAverage(rowType, data) {
        if (this.displaysAverageAsTotal(rowType)) {
          return this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.average) : this.parseCurrency(data.average);  
        } 
        return this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.total) : this.parseCurrency(data.total);
      },
      getRowKey(rowData) {
        return this.generateRowKey(rowData.rowType, rowData.unitTypeId, rowData.year);
      },
      generateRowKey(rowType, unitTypeId, year) {
        return unitTypeId ? `${unitTypeId.toLowerCase()}-${rowType}-${year}` : `${rowType}-${year}`;
      },
      displaysAverageAsTotal(rowType) {
        switch(rowType) {
          case SUMMARY_GPR:
          case SUMMARY_LESS_VACANCY_LOSS:
          case SUMMARY_LESS_ACTUAL_RENT:
          case SUMMARY_GAIN_LOSS_TO_LEASE:
          case AVERAGE_GPR:
          case AVERAGE_CONTRACTED_RENT:
          case RENEWAL_LEASES:
          case RENEWAL_RENT_INCREASE_PERCENTAGE:
          case RENEWAL_RENT_INCREASE_AMOUNT:
          case AVERAGE_RENEWAL_RENT:
          case SUBTOTAL_RENEWAL_RENT:
          case NEW_MOVE_IN_LEASES:
          case NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE:
          case NEW_MOVE_IN_RENT_DISCOUNT_AMOUNT:
          case AVERAGE_NEW_MOVE_IN_RENT:
          case SUBTOTAL_NEW_MOVE_IN_RENT:
          case FORECASTED_LEASES:
          case SUBTOTAL_FORECASTED_RENT:
          case ESTIMATED_CONTRACTED_LEASES:
          case SUBTOTAL_ESTIMATED_CONTRACTED_RENT:
          case PROJECTED_ACTUAL_RENT:
            return true;
          default:
            return false;
        }
      },
      displaysMarketRentAbbrevation(rowType) {
        switch(rowType) {
          case RENEWAL_RENT_INCREASE_PERCENTAGE:
            return this.renewAtMarketRent;
          case NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE:
            return this.moveInAtMarketRent;
          default:
            return false;
        }
      },
      roundsTotalOrAverage(rowType) {
        switch(rowType) {
          case RENEWAL_LEASES:
          case NEW_MOVE_IN_LEASES:
          case FORECASTED_LEASES:
          case ESTIMATED_CONTRACTED_LEASES:
            return true;
          default:
            return false;
        }
      },
      selectLabelCellRenderer(params) {
        let showHelpIcon = false;
        let tooltip = '';
        switch (params.data.rowType)
        {
          case SUMMARY_GAIN_LOSS_TO_LEASE:
            showHelpIcon = true;
            tooltip = '<strong>Positive Numbers</strong>: These are a Loss to Lease. They will be displayed in other BudgetsPro fields as negative numbers. They will display as a debit (negative) on the general ledger account.<br/><strong>Negative Numbers</strong>: These are a Gain to Lease. They will be displayed in other BudgetsPro fields as positive numbers. They will display as a credit(positive) on the general ledger account.'
            break;
          case AVERAGE_CONTRACTED_RENT:
            showHelpIcon = true;
            tooltip = 'This is the average rent from the All Units report when viewed as of the last day of the prior budget year. It is calculated using the rent from units that have an active lease during the first budget month that are not in "Denied" status. It uses the same value for the entire budget year for consistency.'
            break;
          case FORECASTED_LEASES:
            showHelpIcon = true;
            tooltip = 'This is the number of existing forecasted leases from prior month renewals and move-ins. Forecasted leases are generally assumed to last through the budget year, but this number is adjusted automatically to account for budgeted move-outs.';
            break;
          case SUBTOTAL_FORECASTED_RENT:
            showHelpIcon = true;
            tooltip = 'This is the cumulative forecasted rent from prior month renewals and move-ins. Forecasted rent is generally assumed to last through the budget year, but this number is adjusted automatically to account for budgeted move-outs.';
            break;
          case ESTIMATED_CONTRACTED_LEASES:
            showHelpIcon = true;
            tooltip = 'This is the estimated number of active lease contracts that will already exist in the ResMan system as of the start of the budget year, and that will not be renewing or moving out during the month. It is calculated by taking the Ending Occupied Units (from the Move Ins/Move Outs tab) and subtracting # Renewals (see above), # New Leases (Move-Ins) (see above), and # Forecasted Leases (see above).';
            break;
          case SUBTOTAL_ESTIMATED_CONTRACTED_RENT:
            showHelpIcon = true;
            tooltip = 'This is the "Average Contracted Rent" (see above) multiplied by the "# Estimated Contracted Leases" (see above).';
            break;
        }

        if (showHelpIcon) {
          return {
            component: 'ConditionalIconCellRenderer',
            params: {
              showIcon: true,
              iconCode: 'mdi-help-circle',
              iconColor: 'var(--v-resBlue-darken1)',
              tooltip: tooltip,
              tooltipOnTop: true,
              tooltipMaxWidth: '400px',
              tooltipNudgeRight: 190,
              subValue: params.data.subLabel
            }
          }
        }

        if (params.data.subLabel) {
          return {
            component: 'LabelCellRenderer',
            params: {
              label: params.data.label,
              subLabel: params.data.subLabel
            }
          }
        }

        return null; // use default renderer
      },
      calculateTotalOrAverage(rowData) {
        if (this.displaysAverageAsTotal(rowData.rowType)) {
          return this.roundsTotalOrAverage(rowData.rowType) ? this.calculateAverageAsInteger(rowData) : this.calculateAverageAsCurrency(rowData);
        }
        return this.roundsTotalOrAverage(rowData.rowType) ? this.calculateTotalAsInteger(rowData) : this.calculateTotalAsCurrency(rowData);
      },
      isSummaryRow(rowType) {
        switch(rowType) {
          case SUMMARY_GPR:
          case SUMMARY_LESS_VACANCY_LOSS:
          case SUMMARY_LESS_ACTUAL_RENT:
          case SUMMARY_GAIN_LOSS_TO_LEASE:
            return true;
          default:
            return false;
        }
      },
      getRowClass(params) {
        switch(params.data.rowType) {
          // readonly top line
          case SUMMARY_GPR:
          case SUMMARY_GAIN_LOSS_TO_LEASE:
          case AVERAGE_GPR:
          case RENEWAL_LEASES:
          case NEW_MOVE_IN_LEASES:
          case FORECASTED_LEASES:
          case ESTIMATED_CONTRACTED_LEASES:
          case PROJECTED_ACTUAL_RENT:
            return 'readonly-row top-lined-row';
          // readonly
          case SUMMARY_LESS_VACANCY_LOSS:
          case SUMMARY_LESS_ACTUAL_RENT:
          case AVERAGE_CONTRACTED_RENT:
          case RENEWAL_RENT_INCREASE_AMOUNT:
          case AVERAGE_RENEWAL_RENT:
          case SUBTOTAL_RENEWAL_RENT:
          case NEW_MOVE_IN_RENT_DISCOUNT_AMOUNT:
          case AVERAGE_NEW_MOVE_IN_RENT:
          case SUBTOTAL_FORECASTED_RENT:
          case SUBTOTAL_ESTIMATED_CONTRACTED_RENT:
          case SUBTOTAL_NEW_MOVE_IN_RENT:
            return 'readonly-row';
          // conditional readonly
          case RENEWAL_RENT_INCREASE_PERCENTAGE:
            return this.renewAtMarketRent || this.readonly ? 'readonly-row' : '';
          case NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE:
            return this.moveInAtMarketRent || this.readonly ? 'readonly-row' : '';
          default:
            return '';
        }
      },
      isRowEditable(rowType) {
        if (this.readonly) {
          return false;
        }
        switch(rowType) {
          case RENEWAL_RENT_INCREASE_PERCENTAGE:
            return this.renewAtMarketRent == false;
          case NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE:
            return this.moveInAtMarketRent == false;
          default:
            return false;
        }
      },
      isCellEditable(params) {
        if (this.readonly) {
          return false;
        }
        if (this.readonly || this.isCalculating) {
          return false; // view is readonly or is currently calculating
        }
        if (!this.isRowEditable(params.data.rowType)) {
          return false; // row type is not editable
        }
        if (this.isPeriodIndexAmountColumn(params.colDef.field)) {
          return true;
        }

        return false;
      },
      // row filtering logic
      shouldShowRow(node) {
        // row should be visible if it is a summary row OR it is tracked at property level OR its associated unit type is not filtered OR it is the unit type header itself)
        return (this.isSummaryRow(node.data.rowType) || !this.trackActualRentAtUnitTypeLevel || !node.data.isUnitTypeCollapsed || node.data.rowType == UNIT_TYPES_HEADER) && node.data.rowType != RENEWAL_RENT_INCREASE_PERCENTAGE;
      },
      refreshDataFilter() {
        this.summaryGridOptions.api.onFilterChanged();
        this.detailGridOptions.api.onFilterChanged();
      },
      // row update logic
      async onCellValueChanged(params) {
        let t = this;

        // do not need to recalculate if a row changed that users cannot edit
        if (!t.isRowEditable(params.data.rowType)) {
          return;
        }
        // do not need to recalculate if the cell value did not actually change
        if (params.oldValue == params.newValue) {
          return;
        }
        // do not need to recalculate if a cell value changed that was not a period index amount
        if (!t.isPeriodIndexAmountColumn(params.colDef.field)) {
          return;
        }

        t.isCalculating = true;

        // prepare for calculations
        let periodIndex = t.getPeriodIndexFromColumn(params.colDef.field);
        let rowNodeDataToUpdate = []; // track updated rows

        // propagate value to the rest of the months
        let val = t.parseCurrency(params.newValue);
        for (let i = periodIndex; i < 12; i++) {
          t.setRowNodePeriodIndexAmount(params.data, i, val);
        }

        // update totals
        params.data.totalOrAverage = t.calculateTotalOrAverage(params.data);

        // prepare updates for the front-end
        rowNodeDataToUpdate.push(params.data);

        // save updates to the front-end
        if (rowNodeDataToUpdate.length > 0) {
          t.detailGridOptions.api.applyTransactionAsync({ update: rowNodeDataToUpdate });
        }

        // prepare updates for the backend
        let propertyEntriesToPost = [];
        let unitTypeEntriesToPost = [];
        rowNodeDataToUpdate.filter(e => e.worksheetEntryTypeId).forEach((e) => {
          let entry = {
            unitTypeId: e.unitTypeId,
            worksheetEntryTypeId: e.worksheetEntryTypeId,
            periodIndex00: e.periodIndex00Amount,
            periodIndex01: e.periodIndex01Amount,
            periodIndex02: e.periodIndex02Amount,
            periodIndex03: e.periodIndex03Amount,
            periodIndex04: e.periodIndex04Amount,
            periodIndex05: e.periodIndex05Amount,
            periodIndex06: e.periodIndex06Amount,
            periodIndex07: e.periodIndex07Amount,
            periodIndex08: e.periodIndex08Amount,
            periodIndex09: e.periodIndex09Amount,
            periodIndex10: e.periodIndex10Amount,
            periodIndex11: e.periodIndex11Amount
          };
          
          if (!t.trackActualRentAtUnitTypeLevel) {
            propertyEntriesToPost.push(entry);
          } else {
            unitTypeEntriesToPost.push(entry);
          }
        });

        // send updates to the backend
        if (propertyEntriesToPost.length > 0) {
          await worksheetService.updatePropertyWorksheetEntries(t.budgetIterationId, propertyEntriesToPost, t.budgetIterationLastModifiedTimestamp);
        }
        if (unitTypeEntriesToPost.length > 0) {
          await worksheetService.updateUnitTypeWorksheetEntries(t.budgetIterationId, unitTypeEntriesToPost, t.budgetIterationLastModifiedTimestamp);
        }

        await t.refreshActualRentData();

        t.isCalculating = false;

        // refresh dependencies
        if (!t.summaryRefreshIsQueued) {
          t.summaryRefreshIsQueued = true;
          setTimeout(async () => {
            t.summaryRefreshIsQueued = false;
            t.$eventHub.$emit('actualRentEntriesUpdated', this.budgetIterationId);
            var worksheetEntryTypeIds = [16, 17];
            /*
                Enums.WorksheetEntryTypes.RenewalRentIncreasePercentage,
                Enums.WorksheetEntryTypes.NewMoveInsRentDiscountPercentage
            */
            await budgetGLAccountService.updateBasedOnWorksheets(t.budgetIterationId, worksheetEntryTypeIds, [], t.budgetIterationLastModifiedTimestamp);
          }, 2500); // reduce refresh calls when multiple edits happen at once
        }
      },
      cellFormatter(params) {
        if (this.displaysMarketRentAbbrevation(params.data.rowType)) {
          return Constants.MARKET_RENT_ABBREVIATION_CELL_VALUE;
        }

        switch (params.data.rowType) {
          // percentages
          case RENEWAL_RENT_INCREASE_PERCENTAGE:
          case NEW_MOVE_IN_RENT_DISCOUNT_PERCENTAGE:
            return this.formatPercentage(params.value, true, true);
          // dollar amounts
          case SUMMARY_GPR:
          case SUMMARY_LESS_VACANCY_LOSS:
          case SUMMARY_LESS_ACTUAL_RENT:
          case SUMMARY_GAIN_LOSS_TO_LEASE:
          case AVERAGE_GPR:
          case AVERAGE_CONTRACTED_RENT:
          case RENEWAL_RENT_INCREASE_AMOUNT:
          case AVERAGE_RENEWAL_RENT:
          case SUBTOTAL_RENEWAL_RENT:
          case NEW_MOVE_IN_RENT_DISCOUNT_AMOUNT:
          case AVERAGE_NEW_MOVE_IN_RENT:
          case SUBTOTAL_NEW_MOVE_IN_RENT:
          case SUBTOTAL_FORECASTED_RENT:
          case SUBTOTAL_ESTIMATED_CONTRACTED_RENT:
          case PROJECTED_ACTUAL_RENT:
            return this.formatCurrency(params.value, true, true);
          // integers
          case RENEWAL_LEASES:
          case NEW_MOVE_IN_LEASES:
          case FORECASTED_LEASES:
          case ESTIMATED_CONTRACTED_LEASES:
            return this.formatInteger(params.value);
          default:
            return params.value?.toString();
        }
      },
      // clear and reset logic
      openClearResetDialog() {
        if (this.readonly) {
          this.$emit('denied-status');
          return;
        }
        this.showClearResetDialog = true;
      },
      closeClearResetDialog() {
        this.showClearResetDialog = false;
      },
      async confirmClearResetDialog() {
        this.resetActualRentData(false);
        this.closeClearResetDialog();
      },
      async resetActualRentData(trackingLevelChanged) {
        this.changeLoading(true);

        let response = await occupancyService.setDefaultActualRentEntries(this.budgetIterationId);
        if (response) {
          if (trackingLevelChanged == true) {
            this.showGridLoadingOverlay(this.summaryGridOptions);
            this.showGridLoadingOverlay(this.detailGridOptions);
            await this.loadActualRentData(); // have to completely reload the grid if switching the tracking level
            this.hideGridLoadingOverlay(this.summaryGridOptions);
            this.hideGridLoadingOverlay(this.detailGridOptions);
          } else {
            await this.refreshActualRentData();
          }
          
          this.$eventHub.$emit('actualRentEntriesUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Actual Rent entries cleared and reset successfully!');
        }
        else {
          this.activateErrorDialog({title: 'Clear & Reset Failed', message: 'An error occurred clearing and resetting the data. Please try again.'});
        }

        this.changeLoading(false);
      },
      // change tracking level logic (show actual rent at property or unit type level)
      trackActualRentAtUnitTypeLevelChanged(trackingLevel) {
        this.trackActualRentAtUnitTypeLevel = trackingLevel;
        this.openChangeTrackingLevelDialog();
      },
      openChangeTrackingLevelDialog() {
        this.showChangeTrackingLevelDialog = true;
      },
      closeChangeTrackingLevelDialog() {
        this.showChangeTrackingLevelDialog = false;
      },
      async confirmChangeTrackingLevelDialog() {
        let worksheetParameters = { 
          trackActualRentAtUnitTypeLevel: this.trackActualRentAtUnitTypeLevel 
        };
        let response = await worksheetService.updateWorksheetParameters(this.budgetIterationId, worksheetParameters, this.budgetIterationLastModifiedTimestamp);
        if (response.lastModifiedTimestamp) {
          this.budgetIterationLastModifiedTimestamp = response.lastModifiedTimestamp; // update timestamp
        }
        this.resetActualRentData(true);
        this.closeChangeTrackingLevelDialog();
      },
      async cancelChangeTrackingLevelDialog() {
        this.trackActualRentAtUnitTypeLevel = !this.trackActualRentAtUnitTypeLevel; // toggle back to what it was previously
        this.closeChangeTrackingLevelDialog();
      },
      // comments logic
      openComments() {
        this.$emit('openComments', 'actualrent');
      },
      async refreshCommentIcon() {
        var commentsResults = await worksheetService.commentsExist(this.budgetIterationId, 6);
        this.tabHasUnresolvedComments = commentsResults.unresolvedFlagExists;
        this.tabHasComments = commentsResults.hasComments;
        console.debug('refresh comment actual rent');
      },
    },
    computed: {
      currentHeaders() { 
        return this.availableColumns;
      },
      currentSummaryData() {
        return this.summaryData;
      },
      currentDetailData() {
        return this.detailData;
      },
      commentIconColor() {
        return this.tabHasUnresolvedComments ? 'var(--v-resRed-darken1)' : 'var(--v-resBlue-darken1)';
      },
      commentIcon() {
        return this.tabHasUnresolvedComments ? 'mdi-flag' : this.tabHasComments ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline';
      },
      menuIcon() {
        return this.menuOpen ? 'mdi-dots-horizontal-circle' : 'mdi-dots-horizontal';
      },
      menuColor() {
        return 'var(--v-resBlue-darken1)';
      }
    },
    watch: {
      commentIteration: function () {
        this.refreshCommentIcon();
      }
    },
    async mounted() {
      await this.load();
      await this.refreshCommentIcon();
    },
    async created() {
      let t = this;

      t.$eventHub.$on('modelAdminDownEntriesUpdated', async (budgetIterationId) => {
        if (budgetIterationId == t.budgetIterationId) {
          await t.refreshActualRentData();
        }
      });
      t.$eventHub.$on('moveInMoveOutEntriesUpdated', async (budgetIterationId) => {
        if (budgetIterationId == t.budgetIterationId) {
          await t.refreshActualRentData();
        }
      });
    }
  };
</script>

<style lang="scss" scoped>
.v-input--selection-controls::v-deep {
  margin: 0 !important;
  padding: 0 !important;
}
.grid-controls {
  height: 40px;
  margin-top: 14px;
  margin-bottom: 14px;
}
.historicals-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
.add-columns-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  vertical-align: top; 
  height: 100%;
}
.add-columns-label {
  vertical-align: top;
  display: inline-block;
  padding: 7px 8px 0 10px; 
  height: 100%;
}
.add-columns-option::v-deep {
  display: inline-block;
  .v-label {
    font-size: 13px !important;
    color: var(--v-resGrey-darken2);
    font-weight: bold;
    padding-right: 14px;
    padding-top: 8px;
  }
  .v-input--selection-controls__input {
    margin-top: 0;
    padding-top: 8px;
  }
  .v-messages {
    display: none;
  }
}
.help-controls {
  height: 100%;
  float: right;
  border: solid 1px var(--v-resGrey-lighten3);
  padding: 7px 6px 6px 6px;
    cursor: pointer;
}
.ellipses-control {
  height: 20px;
  width: 20px;
  margin-left: 10px;
  margin-bottom: 8px;
}
.ellipses-control-option-list {
  border-radius: 6px;
  border: 1px solid var(--v-resBlue-darken1);
}
.ellipses-control-option {
  font-size: 13px;
}
.level-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
</style>
